import * as React from "react";
import { Form, FormButton, FormDropdown, FormField, FormInput, Message } from "@/components/bp/bp-ui";
import { FormikProps, Formik } from "formik";
import { FormErrorList } from "../FormErrorList";
import { AlleleFormat, CaseClient, EvidenceClient, EvidenceReadModel2, LabKitClient } from "@app/api2/api";
import * as yup from "yup";
import SemanticField from "../../SemanticField";
import { FileUpload, IUploadedFile } from "../FileUpload";
import { PageHeader } from "@common/PageHeader";
import { insertComponentsIntoText } from "@common/Utils";
import { IDropdownItem } from "../IDropdownItem";
import { ChangeObserver } from "../Labs/ChangeObserver";
import { getCaseAlleleFormat, toAlleleFormatName } from "@common/AlleleFormatNames";
import { TranslationContext } from "../Contexts/TranslationContext";
import { BPContainer } from "@/components/bp/bpcontainer";

export class EvidenceEditProps {
    public evidenceId: number;
    public labId: number;
    public caseId: number;
    public genGraph: boolean;
}

export class EvidenceEditFormValues {
    public kitId: number;
    public name: string;
    public file: IUploadedFile;
    public format?: AlleleFormat;
}

export class EvidenceEditState {
    public formData: EvidenceEditFormValues = new EvidenceEditFormValues();
    public loading: boolean = true;
    public kitList: Array<IDropdownItem<number>> = [];
    serverErrors: string[];
    showServerErrors: boolean = false;

    previewErrors: string[];
    showPreviewErrors: boolean = false;

    //previewWarnings: string[] = [];

    showEvidenceNames: boolean = false;
    evidenceNames: Array<{ name: string, message: string }> = [];

    evidence: EvidenceReadModel2;
    caseUrl: string;
    isUAS: boolean;
    caseAlleleFormat: AlleleFormat;
}


export class EvidenceEdit extends React.Component<EvidenceEditProps, EvidenceEditState> {
    

    private createSchema = yup.object().shape({
        kitId: yup.number().required().label(window.translation.common.kit),
        file: yup.object().required().shape({
            fileName: yup.string().required(window.translation.evidence.evidenceFile),
        }).nullable(),
    });
    private editSchema = yup.object().shape({
        kitId: yup.number().required().label(window.translation.common.kit),
        name: yup.string().required().label(window.translation.evidence.name),
    });

    private kitClient = new LabKitClient();
    private caseClient = new CaseClient();
    private evidenceClient = new EvidenceClient();

    constructor(props, context) {
        super(props, context);
        

        this.state = new EvidenceEditState();
    }

    public render() {

        const caseName = this.state?.evidence?.cases?.length > 0 ? this.state.evidence.cases[0].name : ''

        if (this.props.evidenceId) {
            return (
                <BPContainer>
                    <PageHeader header={insertComponentsIntoText(window.translation.evidenceView.editTitle, {
                        '{evidenceName}': this.state.formData.name,
                        '{caseName}': this.state.evidence ? <a key={this.state.evidence.id} href={this.state.caseUrl}>{caseName}</a> : '--'
                    })} />
                    <Formik isInitialValid={true} enableReinitialize={true} initialValues={this.state.formData} onSubmit={this.submitEdit} validationSchema={this.editSchema}>
                        {(props) => {
                            return this.renderEditForm(props)
                        }}
                    </Formik>
                </BPContainer>
            );

        } else {
            return (
                <BPContainer>
                    <PageHeader header={insertComponentsIntoText(window.translation.evidenceView.uploadTitle, {
                        '{caseName}': this.state.evidence ? <a key={this.state.evidence.id} href={this.state.caseUrl}>{caseName}</a> : '--'
                    })} />
                    <Formik initialValues={this.state.formData} onSubmit={this.submitCreate} validationSchema={this.createSchema}>
                        {(props) => {
                            return this.renderCreateForm(props)
                        }}
                    </Formik>
                </BPContainer>
            );
        }
    }

    public async componentDidMount(): Promise<any> {
        const kits = await this.caseClient.getCaseKits(this.props.labId, this.props.caseId);

        if (this.props.evidenceId) {
            const evidence = await this.evidenceClient.getById(this.props.labId, this.props.caseId, this.props.evidenceId);

            this.setState({
                evidence: evidence.evidence,
                caseUrl: evidence.caseUrl,
                formData: {
                    name: evidence.evidence.name,
                    kitId: evidence.evidence.kitId,
                    file: null
                }
            });
        } else {
            const evidence = await this.evidenceClient.getForUpload(this.props.labId, this.props.caseId);
            let alleleFormat = await getCaseAlleleFormat({ labId: this.props.labId, caseId: this.props.caseId })

            this.setState({
                caseUrl: evidence.caseUrl,
                evidence: { cases: [{ id: null, name: evidence.caseName}], id: null, kitId: null },
                caseAlleleFormat: alleleFormat

            });
        }


        this.setState({
            loading: false,
            kitList: kits.map((k) => ({ key: k.id, value: k.id, text: k.fullName })),
        });
    }

    private clearFilePreview = async () => {
        this.setState({
            isUAS: false,
            showPreviewErrors: false,
            showEvidenceNames: false,
            evidenceNames: [],
            previewErrors: []
        });
    }

    private getFilePreview = async (file: IUploadedFile, kitId: number, format: AlleleFormat) => {
        const result = await this.evidenceClient.preview(this.props.labId, this.props.caseId,
            {
                kitId: kitId,
                evidenceFile: file.contents,
                format: format
            });

        if (!result.successful) {
            this.setState({
                isUAS: result.isUAS,
                showPreviewErrors: (result.errors || []).length > 0,
                showEvidenceNames: false,
                evidenceNames: [],
                previewErrors: (result.errors || []).map((e) => (e.value))
            });
        } else {
            this.setState({
                isUAS: result.isUAS,
                showPreviewErrors: (result.errors || []).length > 0,
                showEvidenceNames: true,
                previewErrors: (result.errors || []).map((e) => (e.value)),
                evidenceNames: result.newEvidence.map((e) => {
                    return { name: e.name, message: e.warning };
                })
            });
        }

    };

    private renderCreateForm = (props: FormikProps<EvidenceEditFormValues>) => {
        const handleChange = async (name: string, data: IUploadedFile) => {
            await props.setFieldTouched(name);
            await props.setFieldValue(name, data);
            this.setState({ formData: { ...this.state.formData, [name]: data } });
        };

        const onFormChange = async (values: EvidenceEditFormValues) => {
            if (values.file)
                await this.getFilePreview(values.file, values.kitId, values.format)
            else
                await this.clearFilePreview()
        }

        const formats = [this.state.caseAlleleFormat].map(x => {
            return {
                key: `form${x}`, value: x, text: toAlleleFormatName(x)
            }
        })

        const canSubmit = () => {
            return props.isValid && (!this.state.showPreviewErrors);
        };

        const errorList = Object.getOwnPropertyNames(props.errors)
            .filter((k) => props.touched[k])
            .map((k) => props.errors[k]);

        return (<TranslationContext.Consumer>{(t) => <Form loading={this.state.loading} onSubmit={props.handleSubmit}>
            <div className="ui segment">
                <ChangeObserver value={props.values} onChange={values => onFormChange(values)} />

                <SemanticField component={FormDropdown} name="kitId" selection options={this.state.kitList} label={t.common.kit} value={props.values.kitId} error={props.errors.kitId && props.touched.kitId} />
                <FormField>
                    <label>{t.evidence.evidenceFile}</label>
                    <FileUpload placeholderText={t.evidence.evidenceFile} name="file" fileUploaded={handleChange} value={props.values.file} />
                </FormField>
                {this.state.isUAS &&
                    <SemanticField component={FormDropdown} name="format" selection options={formats} label={t.sample.sampleInUAS} value={props.values.format} error={props.errors.format && props.touched.format} />
                }

                <FormButton disabled={!canSubmit()}>{t.common.save}</FormButton>
                <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                <FormErrorList message={t.evidence.fileErrors} show={this.state.showServerErrors} errors={this.state.serverErrors} />
                <FormErrorList message={t.evidence.fileCannotBeProcessed} show={this.state.showPreviewErrors} errors={this.state.previewErrors} />
                {this.state.showEvidenceNames && <Message info header={t.evidence.evidenceNames} list={this.state.evidenceNames.map(m => `${m.name} ${m.message ? `(${m.message})` : ""}`)} />}
            </div>

        </Form>}</TranslationContext.Consumer>);
    };

    private renderEditForm = (props: FormikProps<EvidenceEditFormValues>) => {
        const errorList = Object.getOwnPropertyNames(props.errors)
            .filter((k) => props.touched[k])
            .map((k) => props.errors[k]);

        return (<TranslationContext.Consumer>{(t) => <Form loading={this.state.loading} onSubmit={props.handleSubmit}>
            <div className="ui segment">
                <SemanticField component={FormDropdown} name="kitId" selection options={this.state.kitList} label={t.common.kit} value={props.values.kitId} error={props.errors.kitId && props.touched.kitId} />
                <SemanticField component={FormInput} name="name" label={t.evidence.name} value={props.values.name} error={props.errors.name && props.touched.name} />
                <FormButton disabled={!props.isValid}>{t.common.save}</FormButton>
                <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                <FormErrorList message={t.evidence.fileErrors} show={this.state.showServerErrors} errors={this.state.serverErrors} />
            </div>

        </Form>}</TranslationContext.Consumer>);
    };

    private submitCreate = async (values: EvidenceEditFormValues): Promise<any> => {
        this.setState({ loading: true });
        const result = await this.evidenceClient.create(this.props.labId, this.props.caseId,
            {
                kitId: values.kitId,
                evidenceFile: values.file.contents,
                format: values.format
            });

        if (result.successful) {
            window.location.assign(result.url);
        } else {
            this.setState({
                loading: false,
                serverErrors: result.errors.map((e) => e.value),
                showServerErrors: true,
            });
        }
    };

    private submitEdit = async (values: EvidenceEditFormValues): Promise<any> => {
        this.setState({ loading: true });
        const result = await this.evidenceClient.update(this.props.labId, this.props.caseId, this.props.evidenceId,
            {
                kitId: values.kitId,
                name: values.name,
                genGraph: this.props.genGraph,

            });

        if (result.success) {
            window.location.assign(result.url);
        } else {
            this.setState({
                loading: false,
                showServerErrors: true,
            });
        }
    };
}
